import {
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { LinkRounded } from "@material-ui/icons";
import { Alert } from "@material-ui/lab";
import * as format from "date-fns/format";
import * as React from "react";
import { Link } from "react-router-dom";
import Component from "src/components/Component";
import withAxios, { IAxiosProps } from "src/components/withAxios";
import resolveUri from "src/resolveUri";
import { ICoach, ISchool, ISurvey } from "src/services";
import { IDirectorParams } from "src/services";
import "./EmbedCoachTable.css";

interface IPublicProps {
  coaches: ICoach[];
  school: ISchool;
}

type IProps = IPublicProps & IAxiosProps;

interface IState {
  surveys: ISurvey[];
  loading: boolean;
}

interface ICopyState {
  open: boolean;
  message: string;
  severity: "success" | "error";
}

const CLOSED: ICopyState = { open: false, message: "", severity: "success" };

const NumberOfDays = 3;

const CopyLink: React.FC<{ uri: string; label: string }> = ({ uri, label }) => {
  const [snack, setSnack] = React.useState<ICopyState>(CLOSED);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(uri);
      setSnack({ open: true, message: `${label} link copied!`, severity: "success" });
    } catch {
      setSnack({ open: true, message: "Failed to copy", severity: "error" });
    }
  };

  return (
    <>
      <Box display="flex" alignItems="center" gridGap="4px" style={{ whiteSpace: "nowrap" }}>
        <Typography variant="body2" style={{ color: "#494b8f", fontWeight: 500 }}>
          {label}
        </Typography>
        <Tooltip title="Copy link" placement="top">
          <Box
            onClick={handleCopy}
            style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
          >
            <LinkRounded color="primary" fontSize="small" />
          </Box>
        </Tooltip>
      </Box>
      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(CLOSED)}>
        <Alert onClose={() => setSnack(CLOSED)} severity={snack.severity}>
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default withAxios<IPublicProps>(
  class EmbedCoachTable extends Component<IProps, IState> {
    public readonly state: IState = { surveys: [], loading: false };

    public async componentDidMount() {
      await this.fetchSurveys();
    }

    public render() {
      const { coaches, school } = this.props;
      const { loading } = this.state;
      const baseUri: string = window.location.origin;
      const coachesByLatestSurvey = [...coaches].sort((a, b) => {
        const aStartDate = this.getSurveyForCoach(a)?.startDate ?? 0;
        const bStartDate = this.getSurveyForCoach(b)?.startDate ?? 0;
        return bStartDate - aStartDate;
      });

      if (coaches.length === 0) { return null; }

      return (
        <Box marginTop="2rem">
          <Box display="flex" alignItems="center" gridGap="1rem" marginBottom="0.75rem">
            <Typography
              variant="h6"
              className="embed-coach-table-title"
              style={{
                fontWeight: 600,
                color: "#494b8f",
                borderBottom: "2px solid #494b8f",
                paddingBottom: "0.4rem",
                flex: 1,
              }}
            >
              External Coaches (Registered via Embed)
            </Typography>
            {loading && <CircularProgress size={20} />}
          </Box>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell style={{ width: "18%" }}>Coach Name</TableCell>
                <TableCell style={{ width: "20%" }}>Email(s)</TableCell>
                <TableCell style={{ width: "12%" }}>Survey Links</TableCell>
                <TableCell style={{ width: "12%" }}>Survey Start Date</TableCell>
                <TableCell style={{ width: "12%" }}>Survey End Date</TableCell>
                <TableCell style={{ width: "10%" }}>Status</TableCell>
                <TableCell style={{ width: "16%" }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Hide inactive embed registrations from the dashboard list. */}
              {coachesByLatestSurvey.filter((coach) => coach.active).map((coach) => {
                const survey = this.getSurveyForCoach(coach);

                const studentUri = survey
                  ? resolveUri(`${baseUri}/${encodeURIComponent("student")}/${encodeURIComponent(survey.studentCode)}`)
                  : "";
                const coachUri = survey
                  ? resolveUri(`${baseUri}/${encodeURIComponent("coach")}/${encodeURIComponent(survey.coachCode)}`)
                  : "";
                // const parentUri = survey && survey.enableParentQuestions
                //   ? resolveUri(`${baseUri}/${encodeURIComponent("parent")}/${encodeURIComponent(survey.parentCode)}`)
                //   : "";

                const surveyDate = survey
                  ? format(survey.startDate, "MMM DD, YYYY")
                  : "—";

                // add 7 days to the survey start date
                const surveyEndDate = survey
                  ? format(survey.startDate + NumberOfDays * 24 * 60 * 60 * 1000, "MMM DD, YYYY")
                  : "—";

                const isComplete = survey?.responseCollectionEndDate ? true : false;

                // Hide internal Champions 101 report archive recipient from the dashboard email list.
                const emailList = (coach.extra_emails ?? [])
                  .map((e) => e.email)
                  .filter((email) => email !== "coachesacademyreports@gmail.com")
                  .join(", ");

                return (
                  <TableRow key={coach.id}>
                    <TableCell>
                      <Typography variant="body2" style={{ fontWeight: 500 }}>
                        {coach.name}
                      </Typography>
                      {!coach.active && (
                        <Typography variant="caption" color="textSecondary">
                          Inactive
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      <Typography variant="caption" style={{ wordBreak: "break-all" }}>
                        {emailList || "—"}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      {loading ? (
                        <Typography variant="caption" color="textSecondary">Loading…</Typography>
                      ) : survey ? (
                        <Box display="flex" flexDirection="column" gridGap="4px">
                          {(survey.entryLevel === 2 || survey.entryLevel === 4) && (
                            <CopyLink uri={studentUri} label="Student" />
                          )}
                          {(survey.entryLevel === 1 || survey.entryLevel === 3) && (
                            <CopyLink uri={coachUri} label="Coach" />
                          )}
                          {/* {survey.enableParentQuestions && parentUri && (
                            <CopyLink uri={parentUri} label="Parent" />
                          )} */}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="textSecondary">
                          No survey started
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">{surveyDate}</Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">{surveyEndDate}</Typography>
                    </TableCell>

                    <TableCell>
                      {isComplete ? (
                        <Typography variant="caption" style={{ color: "#4caf50", fontWeight: 600 }}>
                          Completed
                        </Typography>
                      ) : survey ? (
                        <Typography variant="caption" style={{ color: "#ff9800", fontWeight: 600 }}>
                          In Progress
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="textSecondary">—</Typography>
                      )}
                    </TableCell>

                    <TableCell align="right">
                      {survey && (
                        <Button
                          size="small"
                          color="primary"
                          variant="contained"
                          component={Link}
                          {...({
                            to: `/${school.slug}/evaluation/${survey.id}/${survey.sportId}/${coach.id}`,
                          } as any)}
                        >
                          View Results
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      );
    }

    private fetchSurveys = async () => {
      const { school } = this.props;
      this.setState({ loading: true });
      try {
        const surveys = await this.buildGetWithParams<ISurvey[], IDirectorParams>(
          this,
          "survey"
        )({ schoolSlug: school.slug });
        this.setState({ surveys: surveys ?? [] });
      } catch (err) {
        console.error("EmbedCoachTable: failed to load surveys", err);
      } finally {
        this.setState({ loading: false });
      }
    };

    private getSurveyForCoach = (coach: ICoach): ISurvey | undefined => {
      const { surveys } = this.state;
      const byCoachId = surveys.filter(
        (s) => s.coachId !== undefined && s.coachId === coach.id
      );
      if (byCoachId.length > 0) {
        return byCoachId.sort((a, b) => b.startDate - a.startDate)[0];
      }
      const byCoachIds = surveys.filter(
        (s) => s.coachIds && s.coachIds.includes(coach.id)
      );
      if (byCoachIds.length > 0) {
        return byCoachIds.sort((a, b) => b.startDate - a.startDate)[0];
      }
      return undefined;
    };
  }
);



// WEBPACK FOOTER //
// ./src/pages/director/dashboard/components/embed-coach-table/EmbedCoachTable.tsx